import { useCallback, useEffect, useRef } from 'react';

type MomentaryEvent<E extends HTMLElement> = React.MouseEvent<E> | React.TouchEvent<E> | React.KeyboardEvent<E> | React.FocusEvent<E>;
type MomentaryEventHandler<E extends HTMLElement> = (e?: MomentaryEvent<E>) => void;
type OnDownEventHandler<E extends HTMLElement> = MomentaryEventHandler<E>;
type OnUpEventHandler<E extends HTMLElement> = MomentaryEventHandler<E>;
type OnClickEventHandler<E extends HTMLElement> = MomentaryEventHandler<E>;
type MomentaryResult<E extends HTMLElement> = [OnDownEventHandler<E>, OnUpEventHandler<E>, OnClickEventHandler<E>] & {
  onDown: OnDownEventHandler<E>;
  onUp: OnUpEventHandler<E>;
  onClick: OnClickEventHandler<E>;
};

/***
 * Hook which implements a momentary button state of pressed/released
 *
 * When "pressed", it will call the callback immediately, and then regularly at the interval.
 *
 * When "released", it will stop calling the callback
 *
 *
 * Usage:
 * pass onDown to onMouseDown, onTouchStart
 * pass onUp to onMouseUp, onTouchEnd, onTouchCancel
 * pass onClick to onClick
 */
export function useMomentary<E extends HTMLElement>(callback: () => any, interval = 240): MomentaryResult<E> {
  const timer = useRef<number>();

  // special mutex to prevent double firing of callback on mouseup, mousedown, click cycle
  // using a ref to avoid state updates
  const clickLock = useRef(false);

  const clear = useCallback(() => {
    if (!timer.current) return;
    clearInterval(timer.current);
    timer.current = undefined;
  }, []);

  // cleanup
  useEffect(() => clear, [clear, callback, interval]);

  const onDown: OnDownEventHandler<E> = useCallback(e => {
    // prevent touch event promotion to mouse event
    e?.preventDefault();
    e?.persist();

    // because we prevent touch to click, simulate click
    // safari removes TouchEvent when Touch isn't supported
    if (window.TouchEvent && e?.nativeEvent instanceof TouchEvent) callback();

    clear();

    timer.current = window.setInterval(() => {
      // set to true if event was a MouseEvent
      clickLock.current = e?.nativeEvent instanceof MouseEvent;
      callback();
    }, interval);
  }, [callback, clear, interval]);

  const onUp: OnUpEventHandler<E> = useCallback(e => {
    // prevent touch event promotion to mouse event
    e?.preventDefault();

    clear();
  }, [clear]);

  const onClick: OnClickEventHandler<E> = useCallback(() => {
    // Prevent calls to callback if we are in a mousedown/mouseup cycle
    if (!clickLock.current) callback();

    clear();

    // onclick always happens after onMouseUp (onUp)
    clickLock.current = false;
  }, [clear, callback]);

  return Object.assign([onDown, onUp, onClick], { onDown, onUp, onClick }) as MomentaryResult<E>;
}
